import { useMemo } from "react"
import { useRouter } from "next/router"

import { useWorkspaceContext } from "@/workspaces/scope"
import { useOrganization, useUser } from "@clerk/nextjs"
import { Alert, Button } from "@coval/ui"
import { Github } from "lucide-react"

import { hasCovalAdminAccess } from "@/utils/auth/coval-admin-access"

import { useDashboardFilters } from "../../contexts/DashboardFiltersContext"
import {
  GithubReceiptRangeError,
  GithubReceiptRequestError,
  useGithubReceipts,
} from "../../routes/useGithubReceipts"
import { getEffectiveChartDateRange } from "../../utils/chart/chartWidgetState"
import {
  githubReceiptViewSchema,
  githubReceiptWindowSchema,
} from "../../utils/githubReceiptSchemas"
import { BaseWidget, type WidgetProps } from "../BaseWidget"
import { GithubReceiptsView } from "./GithubReceiptsView"

export class GithubReceiptsWidget extends BaseWidget {
  type = "github_job_receipts"
  title = "GitHub job receipts"
  description = "Live internal operator totals from native GitHub job receipts."
  icon = (<Github className="size-4" />)
  disabled = false
  renderWidget(props: WidgetProps) {
    return <GithubReceiptsContent {...props} />
  }
}

function GithubReceiptsContent({ widget }: WidgetProps) {
  const router = useRouter()
  const { organization, membership } = useOrganization()
  const { user } = useUser()
  const { currentWorkspace } = useWorkspaceContext()
  const { state } = useDashboardFilters()
  const range = useMemo(
    () =>
      getEffectiveChartDateRange({
        globalDateRange: state.dateRange,
        globalDateRangeOverride: state.globalDateRangeOverride,
        isOverrideActive: state.isOverrideActive,
      }),
    [state.dateRange, state.globalDateRangeOverride, state.isOverrideActive],
  )
  const dates = {
    start_date: range.start.toISOString(),
    end_date: range.end.toISOString(),
  }
  const relative = state.isOverrideActive
    ? state.relativeDateRangeOverride
    : null
  const view = githubReceiptViewSchema.safeParse(
    widget.config?.githubReceiptView ?? "jobs_over_time",
  )
  const validDates =
    Boolean(relative) || githubReceiptWindowSchema.safeParse(dates).success
  const routeSlug =
    typeof router.query.slug === "string" ? router.query.slug : ""
  const routeMatchesOrganization =
    Boolean(routeSlug) &&
    Boolean(organization?.slug) &&
    organization?.slug?.toLowerCase() === routeSlug.toLowerCase()
  const organizationSlug = routeMatchesOrganization
    ? (organization?.slug ?? "")
    : routeSlug
  const allowed =
    membership?.role === "org:admin" &&
    routeMatchesOrganization &&
    hasCovalAdminAccess(user?.primaryEmailAddress)
  const query = useGithubReceipts(
    {
      organizationSlug,
      organizationId: organization?.id,
      workspaceId: currentWorkspace?.id ?? "",
      userId: user?.id,
    },
    dates,
    allowed && validDates && view.success,
    relative,
  )
  if (!allowed) {
    return (
      <p className="p-4 text-coval-text-muted">
        GitHub receipts require internal operator access and organization admin
        access.
      </p>
    )
  }
  if (
    query.error instanceof GithubReceiptRequestError &&
    [401, 403, 404].includes(query.error.status)
  ) {
    return <p className="p-4 text-coval-text-muted">{query.error.message}</p>
  }
  if (query.error instanceof GithubReceiptRangeError) {
    return <p className="p-4 text-coval-text-muted">{query.error.message}</p>
  }
  if (!view.success) {
    return (
      <p className="p-4 text-coval-text-muted">
        Configure a valid GitHub receipt view.
      </p>
    )
  }
  if (!validDates) {
    return (
      <p className="p-4 text-coval-text-muted">
        Select a dashboard date range of at most 93 days to view GitHub
        receipts.
      </p>
    )
  }
  if (!currentWorkspace) {
    return (
      <p className="p-4 text-coval-text-muted">
        Select a workspace to load receipts.
      </p>
    )
  }
  return (
    <div className="flex h-full min-h-0 flex-col gap-2 overflow-auto p-3">
      {query.isError && (
        <Alert variant="warning">
          {query.data
            ? "Refresh failed. Showing the last loaded receipts. "
            : ""}
          {query.error.message}
          <Button variant="secondary" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </Alert>
      )}
      {!query.data && !query.isError && (
        <p role="status">Loading GitHub receipts…</p>
      )}
      {state.metadataFilters.length > 0 && (
        <p className="text-xs text-coval-text-muted">
          Conversation metadata filters do not apply to GitHub jobs.
        </p>
      )}
      {query.data && (
        <GithubReceiptsView
          data={query.data}
          view={view.data}
          title={widget.config?.title || widget.title}
          rolling={Boolean(relative)}
        />
      )}
    </div>
  )
}
